<template>
  <div
    v-if="visibleGlobalAlerts.length > 0"
    class="alert__wrapper"
  >
    <GlobalAlert
      v-for="(alert, index) in globalAlerts"
      :key="index"
      :alert="alert"
      @closeAlert="closeAlert"
    />
  </div>
  <div
    v-if="visibleToastAlerts.length > 0"
    class="toasts__wrapper"
  >
    <ToastAlert
      v-for="(alert, index) in shownToastAlerts"
      :key="index"
      :alert="alert"
      @closeAlert="closeToastAlert(alert)"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Watch } from "vue-property-decorator"
import { Action, Getter } from "s-vuex-class"

import { Alert } from "@/models"
import { Actions, Getters } from "@/store"

import GlobalAlert from "../Alert/GlobalAlert.vue"
import ToastAlert from "../Alert/ToastAlert.vue"

const MAX_SHOWN_TOASTS = 3

@Options({
  name: "Alert",
  components: {
    GlobalAlert,
    ToastAlert
  }
})
export default class extends Vue {
  @Getter(Getters.GET_ALERTS) alerts: Alert[]
  @Action(Actions.CLEAR_REMOVED_ALERTS) clearAlerts: () => void

  @Watch("alerts")
  onStateChanged(newChanges: Alert[], alerts: Alert[]): void {
    if (alerts.length < newChanges.length) {
      this.runTimeout()
    }
    const overflowAlerts = newChanges.filter(alert => !alert.queued).slice(0, -MAX_SHOWN_TOASTS)
    overflowAlerts.forEach(alert => newChanges.splice(newChanges.indexOf(alert), 1))
  }

  /** Starts a queued toast's timeout only once it is shown, so it doesn't expire while waiting for its turn. */
  @Watch("shownToastAlerts")
  onShownToastAlertsChanged(): void {
    this.runTimeout()
  }

  get globalAlerts() {
    return this.alerts.filter(alert => alert.theme === "global")
  }

  get visibleGlobalAlerts() {
    return this.globalAlerts.filter(alert => alert.timeout && alert.timeout > 0)
  }

  get toastAlerts() {
    return this.alerts.filter(alert => alert.theme === "toast")
  }

  get visibleToastAlerts() {
    return this.toastAlerts.filter(alert => alert.timeout && alert.timeout > 0)
  }

  /** Queued toasts are shown three at a time; the next one appears when one of them closes. */
  get shownToastAlerts() {
    const shownQueuedAlerts = this.visibleToastAlerts
      .filter(alert => alert.queued)
      .slice(0, MAX_SHOWN_TOASTS)
    return this.visibleToastAlerts.filter(
      alert => !alert.queued || shownQueuedAlerts.includes(alert)
    )
  }

  created() {
    setInterval(this.clearAlerts, 60000)
  }

  private runTimeout() {
    for (let i = 0; i < this.alerts.length; i++) {
      const alert = this.alerts[i]
      if (
        alert &&
        !alert.timeoutStarted &&
        alert.timeout &&
        alert.timeout > 0 &&
        (!alert.queued || this.shownToastAlerts.includes(alert))
      ) {
        alert.timeoutStarted = true
        alert.setupTimeout()
      }
    }
  }
  closeAlert() {
    for (let i = 0; i < this.alerts.length; i++) {
      const alert = this.alerts[i]
      alert.timeout = -1
    }
  }

  /** Closes only the clicked toast, so stacked toasts are dismissed one by one. */
  closeToastAlert(alert: Alert) {
    alert.cancelTimeout()
    alert.hideToast()
  }
}
</script>
